import {faExternalLink} from '@fortawesome/free-solid-svg-icons';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {useEffect, useState} from 'react';
import {Link} from 'react-router-dom';
import {toast} from 'react-toastify';
import {swalConfig, toastConfig} from '@/config';
import useRouteStore from '@/hooks/useRouteStore';
import {generateTitle, generateUrl} from '@/features/tools';

import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import Select from 'react-select';
import Creatable from 'react-select/creatable';
import {getGenres, createSerie, getSerie} from '@/api';
import Input from '@/common/Input';
import RichTextArea from '@/common/RichTextArea';
import BackdropMedia from '@/common/BackdropMedia';
import PosterMedia from '@/common/PosterMedia';

const CreateSerie = () => {
    const mySwal = withReactContent(Swal);
    const [created, setCreated] = useState(false);
    const [validated, setValidated] = useState(false);
    const [serie, setSerie] = useState({
        titulo: '',
        titulo_original: '',
        url: '',
        descripcion: '',
        estado: '',
    });
    const [genres, setGenres] = useState([]);
    const [selectedGenres, setSelectedGenres] = useState([]);
    const [loading, setLoading] = useState(true);
    const [creating, setCreating] = useState(false);
    const setRoute = useRouteStore((state) => state.setRoute);

    const stateOptions = [
        {value: 'En emisión en Turquía', label: 'En emisión en Turquía'},
        {value: 'Finalizada en Turquía', label: 'Finalizada en Turquía'},
        {value: 'Finalizada, pero en traducción', label: 'Finalizada, pero en traducción'},
    ];

    useEffect(() => {
        setRoute([
            {name: 'Inicio', url: '/administracion'},
            {name: 'Series', url: '/administracion/series'},
            {name: 'Nueva serie', url: '/administracion/series/nueva'},
        ]);
    }, []);

    useEffect(() => {
        getGenres().then((res) => {
            setGenres(res.data.map((genre) => ({value: genre.name, label: genre.name})));
            setLoading(false);
        });
    }, []);

    useEffect(() => {
        if (selectedGenres.length > 0 && serie.titulo_original && serie.descripcion && serie.estado) {
            setValidated(true);
        } else {
            setValidated(false);
        }
    }, [selectedGenres, serie]);

    const handleCreateSerie = async () => {
        const res = await mySwal.fire({...swalConfig.confirm, text: `Estás creando la serie ${serie.titulo_original}`});

        if (res.isConfirmed) {
            setCreating(true);
            try {
                const {data} = await createSerie({
                    titulo_original: serie.titulo_original,
                    titulo: serie.titulo,
                    url: serie.url,
                    generos: selectedGenres.map((genre) => genre.value),
                    descripcion: serie.descripcion,
                    estado: serie.estado,
                });
                setSerie(data);
                setCreated(true);
                toast.success('Serie creada correctamente', toastConfig);
            } catch (err) {
                toast.error(err.response?.data?.message || 'Error al crear la serie', toastConfig);
            } finally {
                setCreating(false);
            }
        }
    };

    const refresh = () => {
        getSerie(serie._id)
            .then((res) => {
                setSerie(res.data);
            })
            .catch((err) => {
                toast.error(err.response?.data?.message || 'Error al cargar la serie', toastConfig);
            });
    };

    return (
        <div className='font-montserrat w-full max-h-full'>
            {!loading ? (
                <>
                    <div className='flex items-center justify-between gap-3 pb-1 mb-2 border-b-2'>
                        <h1 className='font-semibold text-xl min-w-0 truncate'>Serie: {serie.titulo_original || 'Nueva serie'}</h1>
                        {!created && (
                            <button
                                hidden={!validated}
                                onClick={handleCreateSerie}
                                disabled={creating}
                                className='flex items-center gap-1.5 bg-red-1 text-white font-semibold text-sm px-4 py-1.5 rounded hover:text-red-1 hover:bg-opacity-5 disabled:opacity-50 disabled:cursor-not-allowed transition-all shrink-0'
                            >
                                {creating && (
                                    <svg className='animate-spin h-3.5 w-3.5' xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24'>
                                        <circle className='opacity-25' cx='12' cy='12' r='10' stroke='currentColor' strokeWidth='4' />
                                        <path className='opacity-75' fill='currentColor' d='M4 12a8 8 0 018-8V0C5.373 0 22 6.477 22 12h-4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z' />
                                    </svg>
                                )}
                                Crear serie
                            </button>
                        )}
                    </div>
                    {!created ? (
                        <>
                            <div className='grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4'>
                                <Input
                                    id='titulo_original'
                                    value={serie.titulo_original}
                                    onChange={(e) =>
                                        setSerie({...serie, titulo_original: e.target.value, titulo: generateTitle(e.target.value), url: generateUrl(e.target.value)})
                                    }
                                    label='Título original'
                                    labelClassName='w-36'
                                />
                                <div className='flex items-center gap-2 font-montserrat text-lg'>
                                    <label htmlFor='genres' className='font-semibold text-red-1 whitespace-nowrap w-36'>
                                        Géneros
                                    </label>
                                    <Creatable
                                        id='genres'
                                        isMulti
                                        options={genres}
                                        value={selectedGenres}
                                        onChange={(v) => setSelectedGenres(v)}
                                        className='w-full relative z-50'
                                    />
                                </div>
                                <Input id='titulo' value={serie.titulo} label='Título' labelClassName='w-36' disabled/>
                                <Input id='url' value={serie.url} label='Url' labelClassName='w-36' disabled/>
                                <div className='flex items-center gap-2 font-montserrat text-lg'>
                                    <label htmlFor='estado' className='font-semibold text-red-1 whitespace-nowrap w-36'>
                                        Estado
                                    </label>
                                    <Select
                                        id='estado'
                                        options={stateOptions}
                                        value={{value: serie.estado, label: serie.estado}}
                                        onChange={(v) => setSerie({...serie, estado: v.value})}
                                        className='w-full relative z-40'
                                    />
                                </div>
                                <div className='md:col-span-2'>
                                    <label htmlFor='description' className='font-semibold text-red-1 text-lg'>
                                        Descripción
                                    </label>
                                    <RichTextArea value={serie.descripcion} onChange={(v) => setSerie({...serie, descripcion: v})}/>
                                </div>
                            </div>
                            <hr className='my-5 p-0'/>
                        </>
                    ) : null}

                    {created ? (
                        <div>
                            <h2 className='font-semibold text-xl pb-1 mb-2'>Media</h2>
                            <p className='text-lg'>Ahora debes agregar un poster y/o un backdrop.</p>
                            <div className='flex gap-5 mb-4'>
                                <a
                                    className='text-[#032541] px-2 py-1 rounded border-2 border-[#032541] font-semibold'
                                    href={`https://www.themoviedb.org/search?query=${serie.titulo_original}`}
                                    target='_blank'
                                >
                                    <FontAwesomeIcon icon={faExternalLink}/>
                                    &nbsp;Buscar en TheMovieDb
                                </a>
                                <a
                                    className='text-[#293a36] px-2 py-1 rounded border-2 border-[#293a36] font-semibold'
                                    href={`https://thetvdb.com/search?query=${serie.titulo_original}`}
                                    target='_blank'
                                >
                                    <FontAwesomeIcon icon={faExternalLink}/>
                                    &nbsp;Buscar en TheTVDb
                                </a>
                            </div>
                            <div className='grid grid-cols-1 sm:grid-cols-2 gap-4'>
                                <PosterMedia source={serie} type='serie' refresh={refresh}/>
                                <BackdropMedia source={serie} type='serie' refresh={refresh}/>
                            </div>
                            <div className='mt-5'>
                                <Link
                                    to={`/administracion/series/${serie._id}`}
                                    className='text-lg text-red-1 px-2 py-1 rounded border-2 border-red-1 font-semibold hover:bg-red-1 hover:text-white transition-all'
                                >
                                    Terminar creación
                                </Link>
                            </div>
                        </div>
                    ) : null}
                </>
            ) : (
                <h1 className='font-semibold text-xl'>
                    <span className='loader2'></span>
                </h1>
            )}
        </div>
    );
};

export default CreateSerie;
